import React from 'react';
import { Star, Zap, Shield, CreditCard, AlertTriangle } from 'lucide-react';
import { CTAButton } from '../ui/CTAButton';
import { Countdown } from '../ui/Countdown';
// Hero image served via weserv.nl proxy as an optimized WebP (~100KB instead of 2.4MB PNG).
// Now that the image is light, we want the browser's preload scanner to discover it as
// early as possible during HTML parse — so we render it directly with eager+high priority.
const HERO_IMG = 'https://images.weserv.nl/?url=cdn.magicpatterns.com/uploads/nCdfyaEEbhWc5s37T69Hk2/mocap_polonia.png&w=900&output=webp&q=78';
export function Hero() {
  const scrollToOffer = () => {
    document.getElementById('offer')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  return <section className="relative w-full min-h-[90vh] md:min-h-screen bg-gradient-to-b from-cream-bg to-cream-alt pt-12 md:pt-20 pb-12 md:pb-16 px-4 md:px-6 overflow-hidden" data-id="element-142">
      <div className="hidden md:block absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gold-accent/10 rounded-full blur-3xl pointer-events-none" data-id="element-143" />

      <div className="max-w-6xl mx-auto relative z-10" data-id="element-144">
        <div className="flex justify-center mb-6" data-id="element-145">
          <div className="inline-flex items-center justify-center flex-wrap gap-2 bg-red-600 text-white px-4 md:px-5 py-2 md:py-2.5 rounded-full shadow-lg font-bold text-xs sm:text-sm md:text-base text-center" data-id="element-146">
            <AlertTriangle className="w-4 h-4 flex-shrink-0" data-id="element-147" />
            <span data-id="element-148">
              UWAGA: Trenerze piłki nożnej, który spędza noce szukając ćwiczeń i
              planów treningowych
            </span>
          </div>
        </div>

        <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-[1.15] font-display font-bold text-center text-navy-primary mb-4 md:mb-6 max-w-4xl mx-auto" data-id="element-149">
          Zaplanuj cały sezon w 7 minut — nie w 7 godzin
        </h1>

        <p className="text-base md:text-lg text-center text-gray-700 mb-8 md:mb-12 max-w-3xl mx-auto leading-relaxed px-2" data-id="element-150">
          Pierwszy w Polsce kompletny system treningowy "Otwórz i Trenuj" — z
          gotowymi ćwiczeniami, schematami i planami dla każdego poziomu — od U6
          do U13. Odzyskaj swój czas i wejdź na wyższy poziom trenowania.
        </p>

        {/* Hero mockup — directly in HTML so the preload scanner picks it up during HTML parse.
            ~100KB optimized WebP via weserv.nl. fetchPriority=high + decoding=async ensures fast LCP. */}
        <div className="flex justify-center mb-6 md:mb-8" data-id="element-151">
          <div className="relative w-full px-4 md:px-0 min-h-[340px] sm:min-h-[420px] md:min-h-[500px] flex items-center justify-center" data-id="element-152">
            <img src={HERO_IMG} alt="FUTBOL GOTOWY: SYSTEM 365 - Kompletny system treningowy" width={800} height={800} decoding="async" loading="eager" fetchPriority="high" className="mx-auto max-w-full h-auto w-full max-h-[340px] sm:max-h-[420px] md:max-h-[500px] object-contain drop-shadow-2xl" data-id="element-153" />
          </div>
        </div>

        <div className="flex flex-col items-center gap-3 mb-8" data-id="element-154">
          <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-3 text-center" data-id="element-155">
            <div className="flex gap-1" data-id="element-156">
              {[...Array(5)].map((_, i) => <Star key={i} className="w-5 h-5 fill-gold-accent text-gold-accent drop-shadow" data-id="element-157" />)}
            </div>
            <span className="text-base md:text-lg font-semibold text-navy-primary" data-id="element-158">
              4.9/5{' '}
              <span className="text-gray-600 font-normal block sm:inline text-sm md:text-base mt-1 sm:mt-0" data-id="element-159">
                (ponad 1 200 potwierdzonych opinii)
              </span>
            </span>
          </div>

          <div className="inline-flex items-center gap-2 bg-navy-primary text-white px-4 md:px-5 py-1.5 md:py-2 rounded-full shadow-md" data-id="element-160">
            <span className="text-sm md:text-base" data-id="element-161">🔥</span>
            <span className="text-xs sm:text-sm md:text-base font-medium" data-id="element-162">
              67 trenerów wybrało ten system dzisiaj
            </span>
          </div>
        </div>

        <div className="flex flex-col items-center gap-5 mb-8 w-full px-4 md:px-0" data-id="element-163">
          <CTAButton onClick={scrollToOffer} size="large" className="w-full sm:w-auto" data-id="element-164">
            ⚽ TAK! Chcę 40 tygodni gotowych planów — 73 ZŁ
          </CTAButton>

          <Countdown data-id="element-165" />

          <div className="flex flex-wrap justify-center gap-3 md:gap-4 text-xs md:text-sm text-gray-600" data-id="element-166">
            <span className="flex items-center gap-1" data-id="element-167">
              <Zap className="w-3.5 h-3.5 md:w-4 md:h-4 text-gold-accent" data-id="element-168" />
              Natychmiastowy dostęp
            </span>
            <span className="flex items-center gap-1" data-id="element-169">
              <Shield className="w-3.5 h-3.5 md:w-4 md:h-4 text-gold-accent" data-id="element-170" />
              Gwarancja 7 dni
            </span>
            <span className="flex items-center gap-1" data-id="element-171">
              <CreditCard className="w-3.5 h-3.5 md:w-4 md:h-4 text-gold-accent" data-id="element-172" />
              Jednorazowa płatność
            </span>
          </div>
        </div>

        <div className="text-center" data-id="element-173">
          <p className="text-navy-primary font-medium text-sm md:text-base" data-id="element-174">
            🇵🇱 Zasób numer 1 wybierany przez polskich trenerów piłki nożnej
          </p>
        </div>
      </div>
    </section>;
}